import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { HiOutlineStar, HiOutlineChatBubbleLeftRight, HiOutlineUserGroup, HiOutlineChartBar } from "react-icons/hi2";

import Loader from "../../../../components/loader";
import SelectUser from "../../../../components/selectUser";
import Pagination from "../../../../components/pagination";
import SelectWorkshop from "./components/SelectWorkshop";
import api from "../../../../services/api";

export default function Rating() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const [userWorkshops, setUserWorkshops] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const filters = {
    page: parseInt(params.get("page")) || 1,
    per_page: parseInt(params.get("per_page")) || 25,
    user_id: params.get("user_id") || undefined,
    workshop_id: params.get("workshop_id") || undefined,
  };

  const getUserWorkshops = async () => {
    setLoading(true);
    try {
      const { data, total: t } = await api.post("/workshop_user/search", filters);
      setUserWorkshops(data || []);
      setTotal(t || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  function setFilter(name, value) {
    if (value) params.set(name, value);
    else params.delete(name);
    navigate(`?${params.toString()}`);
  }

  useEffect(() => {
    getUserWorkshops();
  }, [filters.page, filters.per_page, filters.user_id, filters.workshop_id]);

  // Summary stats
  const rated = userWorkshops.filter((w) => w.feedback_rating);
  const avgRating = rated.length > 0 ? (rated.reduce((sum, w) => sum + w.feedback_rating, 0) / rated.length).toFixed(1) : "—";
  const withComments = userWorkshops.filter((w) => w.feedback_text);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Workshop Ratings & Feedback</h1>
          <p className="text-gray-600">See how people are rating workshops and what feedback they're giving</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <HiOutlineChartBar className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{total}</div>
              <div className="text-xs text-gray-500">Total entries</div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <HiOutlineStar className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{avgRating}</div>
              <div className="text-xs text-gray-500">Avg rating (this page)</div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <HiOutlineUserGroup className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{rated.length}</div>
              <div className="text-xs text-gray-500">With ratings</div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <HiOutlineChatBubbleLeftRight className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{withComments.length}</div>
              <div className="text-xs text-gray-500">With comments</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-64">
              <SelectUser className="projectsInput m-0" value={filters.user_id} onChange={(u) => setFilter("user_id", u?._id)} placeholder="Filter by user..." />
            </div>
            <div className="w-64">
              <SelectWorkshop value={filters.workshop_id} onChange={(e) => setFilter("workshop_id", e?.value)} />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 flex justify-center">
              <Loader />
            </div>
          ) : userWorkshops.length === 0 ? (
            <div className="p-12 text-center">
              <HiOutlineStar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No ratings found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">User</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Workshop</th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Rating</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Comment</th>
                    <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Started</th>
                    <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Finished</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {userWorkshops.map((w) => (
                    <tr
                      key={w._id}
                      onClick={() => window.open(`/selecole/learn/${w.workshop_id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {w.user_avatar && <img src={w.user_avatar} alt="" className="w-7 h-7 rounded-full object-cover" />}
                          <span className="text-sm font-medium text-gray-900">{w.user_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">{w.workshop_name}</span>
                        {w.workshop_version && <span className="text-xs text-gray-400 ml-1">v{w.workshop_version}</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {w.feedback_rating ? (
                          <span className="inline-flex items-center gap-1 text-sm font-semibold text-amber-600 bg-amber-50 px-2.5 py-0.5 rounded-full">
                            <HiOutlineStar className="w-3.5 h-3.5" />
                            {w.feedback_rating}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600 truncate block max-w-[250px]">{w.feedback_text || "—"}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-xs text-gray-400">{(w.created_at || "").slice(0, 10)}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {w.finished_at ? (
                          <span className="text-xs text-green-600 font-medium">{(w.finished_at || "").slice(0, 10)}</span>
                        ) : (
                          <span className="text-xs text-gray-300">In progress</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="flex justify-end">
          <Pagination
            per_page={filters.per_page}
            total={total}
            onNext={() => setFilter("page", +filters.page + 1)}
            onPrevious={() => setFilter("page", +filters.page - 1)}
            currentPage={filters.page}
          />
        </div>
      </div>
    </div>
  );
}
